import { useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { MapContainer, TileLayer, Circle, Polygon, Marker, Popup, useMap, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import Button from '../components/ui/Button'
import { useToast } from '../components/Toast'
import { useIsMobile } from '../hooks/useIsMobile'
import { listGeofences, createGeofence, updateGeofence, deleteGeofence, listGeofenceEvents } from '../api/tankguard'
import { timeAgo } from '../utils/events'

const TEAL = '#14B8A6'
const DEFAULT_CENTER = [49.0, 10.0] // Frankfurt-ish, picked once on first paint
const DEFAULT_ZOOM = 6

function timeFmt(ts) {
  return new Date(ts).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// Lets the parent recenter the map when a fence is picked in the list.
function MapController({ center, zoom }) {
  const map = useMap()
  useEffect(() => {
    if (center) map.setView(center, zoom || map.getZoom())
  }, [center, zoom, map])
  return null
}

// Captures map clicks while in draw mode. Single click for a circle center
// or a polygon point; double-click closes a polygon.
function DrawCapture({ mode, draft, onCircleStep, onPolyPoint, onPolyClose }) {
  useMapEvents({
    click(e) {
      if (mode === 'circle') onCircleStep(e.latlng)
      else if (mode === 'polygon') onPolyPoint(e.latlng)
    },
    dblclick(e) {
      if (mode === 'polygon') {
        e.originalEvent.preventDefault()
        onPolyClose()
      }
    },
    mousemove(e) {
      if (mode === 'circle' && draft?.center && !draft.confirmedRadius) {
        draft.onMove(e.latlng)
      }
    },
  })
  return null
}

function FenceForm({ initial, onSave, onCancel, saving }) {
  const [name, setName] = useState(initial?.name || '')
  const [alertOn, setAlertOn] = useState(initial?.alert_on || 'exit')
  const [active, setActive] = useState(initial?.active ?? true)
  const [err, setErr] = useState('')

  function submit() {
    if (!name.trim() || name.trim().length < 2) { setErr('Name must be at least 2 characters'); return }
    onSave({ name: name.trim(), alert_on: alertOn, active })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <label className="tg-label">Name</label>
        <input className="tg-input" autoFocus value={name} onChange={e => { setName(e.target.value); setErr('') }} placeholder="Main depot" />
        {err && <div className="tg-field-error">{err}</div>}
      </div>
      <div>
        <label className="tg-label">Alert when truck</label>
        <select className="tg-input" value={alertOn} onChange={e => setAlertOn(e.target.value)} style={{ appearance: 'auto' }}>
          <option value="exit">Exits the zone</option>
          <option value="enter">Enters the zone</option>
          <option value="both">Enters or exits</option>
        </select>
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: 'var(--tg-text-secondary)' }}>
        <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
        Active
      </label>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={submit} loading={saving} disabled={saving}>Save</Button>
      </div>
    </div>
  )
}

export default function Geofences() {
  const toast = useToast()
  const mobile = useIsMobile()
  const [fences, setFences] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [editing, setEditing] = useState(null) // fence being edited (form panel)
  const [saving, setSaving] = useState(false)

  // Draw state. mode: null | 'circle' | 'polygon'
  // For circle: { center: {lat, lng}, radius: number, confirmedRadius: bool, onMove }
  // For polygon: { points: [{lat,lng}, ...] }
  const [drawMode, setDrawMode] = useState(null)
  const [circleDraft, setCircleDraft] = useState(null)
  const [polyDraft, setPolyDraft] = useState([])
  const [draftName, setDraftName] = useState('')
  const [draftAlertOn, setDraftAlertOn] = useState('exit')
  const [drawErr, setDrawErr] = useState('')

  const [recenter, setRecenter] = useState(null)

  async function load() {
    setLoading(true)
    try {
      const [f, e] = await Promise.all([listGeofences(), listGeofenceEvents()])
      setFences(f || []); setEvents(e || [])
    } catch (e) { toast.error(e.message); Sentry.captureException(e) }
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  function startCircle() {
    cancelDraw()
    setDrawMode('circle')
    setDrawErr('')
  }
  function startPolygon() {
    cancelDraw()
    setDrawMode('polygon')
    setPolyDraft([])
    setDrawErr('')
  }
  function cancelDraw() {
    setDrawMode(null); setCircleDraft(null); setPolyDraft([]); setDraftName(''); setDraftAlertOn('exit'); setDrawErr('')
  }

  function handleCircleStep(latlng) {
    if (!circleDraft || !circleDraft.center) {
      // First click sets center; mouse-move + second click sets radius.
      const draft = {
        center: latlng,
        radius: 0,
        confirmedRadius: false,
        onMove: pt => setCircleDraft(d => d ? ({ ...d, radius: pt.distanceTo(d.center) }) : d),
      }
      setCircleDraft(draft)
    } else if (!circleDraft.confirmedRadius) {
      const radius = latlng.distanceTo(circleDraft.center)
      setCircleDraft(d => ({ ...d, radius, confirmedRadius: true }))
    }
  }
  function handlePolyPoint(latlng) {
    setPolyDraft(p => [...p, latlng])
  }
  function handlePolyClose() {
    if (polyDraft.length < 3) { setDrawErr('Polygon needs at least 3 points'); return }
    // Close happens implicitly — backend doesn't require duplicate first/last point.
  }

  async function saveDraft() {
    if (!draftName.trim() || draftName.trim().length < 2) { setDrawErr('Name must be at least 2 characters'); return }
    setSaving(true)
    try {
      if (drawMode === 'circle') {
        if (!circleDraft || !circleDraft.confirmedRadius || circleDraft.radius < 10) {
          setDrawErr('Click the center, then click again to set the radius (≥ 10 m)'); setSaving(false); return
        }
        await createGeofence({
          name: draftName.trim(), type: 'circle', alert_on: draftAlertOn,
          center_lat: circleDraft.center.lat, center_lng: circleDraft.center.lng,
          radius_m: Math.round(circleDraft.radius),
        })
      } else {
        if (polyDraft.length < 3) { setDrawErr('Polygon needs at least 3 points'); setSaving(false); return }
        await createGeofence({
          name: draftName.trim(), type: 'polygon', alert_on: draftAlertOn,
          polygon: polyDraft.map(p => [p.lng, p.lat]),
        })
      }
      toast.success('Geofence created')
      cancelDraw()
      await load()
    } catch (e) { toast.error(e.message); Sentry.captureException(e) }
    setSaving(false)
  }

  async function saveEdit(patch) {
    if (!editing) return
    setSaving(true)
    try {
      await updateGeofence(editing.id, patch)
      toast.success('Geofence updated')
      setEditing(null)
      await load()
    } catch (e) { toast.error(e.message); Sentry.captureException(e) }
    setSaving(false)
  }

  async function remove(fence) {
    if (!window.confirm(`Delete "${fence.name}"?`)) return
    try {
      await deleteGeofence(fence.id)
      toast.success('Geofence deleted')
      if (selected === fence.id) setSelected(null)
      await load()
    } catch (e) { toast.error(e.message); Sentry.captureException(e) }
  }

  function focusFence(f) {
    setSelected(f.id)
    if (f.type === 'circle' && f.center_lat != null) {
      setRecenter([f.center_lat, f.center_lng])
    } else if (f.type === 'polygon' && Array.isArray(f.polygon) && f.polygon.length) {
      const [lng, lat] = f.polygon[0]
      setRecenter([lat, lng])
    }
  }

  const initialCenter = useMemo(() => {
    const first = fences.find(f => f.type === 'circle' && f.center_lat != null)
    if (first) return [first.center_lat, first.center_lng]
    const firstPoly = fences.find(f => f.type === 'polygon' && Array.isArray(f.polygon) && f.polygon.length)
    if (firstPoly) { const [lng, lat] = firstPoly.polygon[0]; return [lat, lng] }
    return DEFAULT_CENTER
  }, [fences])

  return (
    <div className="page" style={{ paddingBottom: 40 }}>
      <div className="page-head" style={{ marginBottom: 16 }}>
        <div>
          <h1>Geofences</h1>
          <p>Draw zones around depots and customer sites. Tankguard alerts the moment a truck crosses the boundary.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant={drawMode === 'circle' ? 'primary' : 'secondary'} size="sm" onClick={startCircle} disabled={saving}>Draw circle</Button>
          <Button variant={drawMode === 'polygon' ? 'primary' : 'secondary'} size="sm" onClick={startPolygon} disabled={saving}>Draw polygon</Button>
          {drawMode && <Button variant="ghost" size="sm" onClick={cancelDraw} disabled={saving}>Cancel draw</Button>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 320px', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="tg-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--tg-border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-primary)' }}>
                {drawMode === 'circle' ? 'Click center, then click again to set radius'
                  : drawMode === 'polygon' ? 'Click to add points · double-click to close'
                  : `${fences.length} geofence${fences.length === 1 ? '' : 's'}`}
              </span>
              <span style={{ fontSize: 10, color: 'var(--tg-text-muted)' }}>
                {drawMode === 'circle' && circleDraft ? `radius ${Math.round(circleDraft.radius)} m` : ''}
                {drawMode === 'polygon' ? `${polyDraft.length} pts` : ''}
              </span>
            </div>
            <div style={{ height: 520 }}>
              <MapContainer center={initialCenter} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }} doubleClickZoom={drawMode !== 'polygon'}>
                <TileLayer attribution='© OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <MapController center={recenter} zoom={14} />
                <DrawCapture
                  mode={drawMode}
                  draft={circleDraft}
                  onCircleStep={handleCircleStep}
                  onPolyPoint={handlePolyPoint}
                  onPolyClose={handlePolyClose}
                />
                {fences.map(f => {
                  if (f.type === 'circle' && f.center_lat != null) {
                    return (
                      <Circle
                        key={f.id}
                        center={[f.center_lat, f.center_lng]}
                        radius={f.radius_m}
                        pathOptions={{ color: TEAL, fillColor: TEAL, fillOpacity: selected === f.id ? 0.28 : 0.14, weight: selected === f.id ? 3 : 2 }}
                        eventHandlers={{ click: () => focusFence(f) }}
                      >
                        <Popup>
                          <strong>{f.name}</strong><br />
                          Circle · r {f.radius_m} m<br />
                          Alert on: {f.alert_on}
                        </Popup>
                      </Circle>
                    )
                  }
                  if (f.type === 'polygon' && Array.isArray(f.polygon) && f.polygon.length >= 3) {
                    const positions = f.polygon.map(([lng, lat]) => [lat, lng])
                    return (
                      <Polygon
                        key={f.id}
                        positions={positions}
                        pathOptions={{ color: TEAL, fillColor: TEAL, fillOpacity: selected === f.id ? 0.28 : 0.14, weight: selected === f.id ? 3 : 2 }}
                        eventHandlers={{ click: () => focusFence(f) }}
                      >
                        <Popup>
                          <strong>{f.name}</strong><br />
                          Polygon · {f.polygon.length} pts<br />
                          Alert on: {f.alert_on}
                        </Popup>
                      </Polygon>
                    )
                  }
                  return null
                })}
                {/* Draft overlay */}
                {drawMode === 'circle' && circleDraft?.center && (
                  <Circle center={[circleDraft.center.lat, circleDraft.center.lng]} radius={Math.max(circleDraft.radius || 1, 1)}
                    pathOptions={{ color: '#F59E0B', fillColor: '#F59E0B', fillOpacity: 0.15, weight: 2, dashArray: '6 6' }} />
                )}
                {drawMode === 'polygon' && polyDraft.length >= 2 && (
                  <Polygon positions={polyDraft.map(p => [p.lat, p.lng])}
                    pathOptions={{ color: '#F59E0B', fillColor: '#F59E0B', fillOpacity: 0.15, weight: 2, dashArray: '6 6' }} />
                )}
                {drawMode === 'polygon' && polyDraft.map((p, i) => (
                  <Marker key={i} position={[p.lat, p.lng]} icon={L.divIcon({ html: '<div style="width:8px;height:8px;border-radius:50%;background:#F59E0B;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3)"></div>', className: '', iconSize: [8, 8], iconAnchor: [4, 4] })} />
                ))}
              </MapContainer>
            </div>
          </div>

          {/* Draft save panel */}
          {drawMode && ((drawMode === 'circle' && circleDraft?.confirmedRadius) || (drawMode === 'polygon' && polyDraft.length >= 3)) && (
            <div className="tg-card" style={{ padding: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Save new geofence</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <input className="tg-input" placeholder="Name (e.g. Main depot)" value={draftName} onChange={e => setDraftName(e.target.value)} autoFocus />
                <select className="tg-input" value={draftAlertOn} onChange={e => setDraftAlertOn(e.target.value)} style={{ appearance: 'auto' }}>
                  <option value="exit">Alert on exit</option>
                  <option value="enter">Alert on enter</option>
                  <option value="both">Alert on enter or exit</option>
                </select>
                {drawErr && <div className="tg-field-error">{drawErr}</div>}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button variant="ghost" size="sm" onClick={cancelDraw} disabled={saving}>Discard</Button>
                  <Button variant="primary" size="sm" onClick={saveDraft} loading={saving} disabled={saving}>Save geofence</Button>
                </div>
              </div>
            </div>
          )}

          {/* Recent events feed */}
          <div className="tg-card" style={{ padding: 0 }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--tg-border-default)', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 12, fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-primary)' }}>Recent breaches</span>
              <span style={{ fontSize: 10, color: 'var(--tg-text-muted)' }}>last {Math.min(20, events.length)}</span>
            </div>
            {events.length === 0 && (
              <div style={{ padding: 16, color: 'var(--tg-text-muted)', fontSize: 13 }}>No breaches recorded yet.</div>
            )}
            {events.slice(0, 20).map(ev => (
              <div key={ev.id} style={{ padding: '10px 14px', borderBottom: '0.5px solid var(--tg-border-default)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: ev.event_type === 'exit' ? 'var(--tg-danger-700)' : 'var(--tg-teal-700)' }}>
                    {ev.truck_name} {ev.event_type === 'exit' ? 'left' : 'entered'} {ev.fence_name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tg-text-secondary)', marginTop: 2 }}>{timeFmt(ev.occurred_at)}</div>
                </div>
                <div style={{ fontSize: 10, color: 'var(--tg-text-muted)' }}>{timeAgo(new Date(ev.occurred_at).getTime())}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right rail: fence list */}
        <aside className="tg-card" style={{ padding: 0 }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--tg-border-default)' }}>
            <span style={{ fontSize: 12, fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-primary)' }}>Geofences</span>
          </div>
          {loading && <div style={{ padding: 16, color: 'var(--tg-text-muted)', fontSize: 13 }}>Loading…</div>}
          {!loading && fences.length === 0 && (
            <div style={{ padding: 16, color: 'var(--tg-text-muted)', fontSize: 13 }}>
              No geofences yet. Click <strong>Draw circle</strong> above and tap the map to set the center, then again for the radius.
            </div>
          )}
          {fences.map(f => (
            <div key={f.id} style={{ padding: '12px 14px', borderBottom: '0.5px solid var(--tg-border-default)', cursor: 'pointer', background: selected === f.id ? 'var(--tg-bg-subtle)' : undefined }} onClick={() => focusFence(f)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--tg-text-primary)' }}>{f.name}</div>
                <span style={{ fontSize: 10, color: 'var(--tg-text-muted)' }}>{f.type}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--tg-text-secondary)', marginTop: 3 }}>
                Alert on {f.alert_on} · {f.active ? 'active' : 'paused'}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setEditing(f) }}>Edit</Button>
                <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); updateGeofence(f.id, { active: !f.active }).then(load).catch(err => toast.error(err.message)) }}>
                  {f.active ? 'Pause' : 'Resume'}
                </Button>
                <Button variant="danger" size="sm" onClick={e => { e.stopPropagation(); remove(f) }}>Delete</Button>
              </div>
            </div>
          ))}
        </aside>
      </div>

      {/* Inline edit panel (simple — opens above the rail on small screens). */}
      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setEditing(null)}>
          <div className="tg-card" style={{ width: 360, padding: 18 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--tg-text-primary)' }}>Edit {editing.name}</h3>
            <FenceForm initial={editing} onSave={saveEdit} onCancel={() => setEditing(null)} saving={saving} />
          </div>
        </div>
      )}
    </div>
  )
}
